import React from 'react';
import BucketChart from './BucketChart';
import InfoHint from './InfoHint';

export const percent = (value) => `${(value * 100).toFixed(1)}%`;

export function Metric({ label, value, hint, tone }) {
  return (
    <div className={`metric${tone ? ` metric-${tone}` : ''}`}>
      <span className="metric-value">{value}</span>
      <span className="metric-label">
        {label}
        {hint && <InfoHint text={hint} />}
      </span>
    </div>
  );
}

function BreakdownTable({ rows, title, columns }) {
  if (rows.length === 0) return null;
  return (
    <>
      <h4 className="lead-detail-heading">{title}</h4>
      <table className="admin-table">
        <thead>
          <tr>
            <th>{columns.key}</th>
            <th>{columns.sent}</th>
            <th>{columns.replies}</th>
            <th>{columns.replyRate}</th>
            <th>{columns.bounced}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>{row.label}</td>
              <td>{row.sent}</td>
              <td>{row.replies}</td>
              <td>{percent(row.reply_rate)}</td>
              <td>{row.bounced_hard}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function HourChart({ buckets, title, emptyLabel }) {
  return (
    <div className="bucket-chart">
      <div className="bucket-chart-title">{title}</div>
      <BucketChart
        emptyLabel={emptyLabel}
        buckets={buckets.map((bucket) => ({
          key: bucket.hour,
          label: bucket.hour,
          title: `${bucket.hour}:00 - ${bucket.opens} / ${bucket.clicks}`,
          segments: [
            { key: 'opens', value: bucket.opens, color: 'var(--accent)' },
            { key: 'clicks', value: bucket.clicks, color: '#7c3aed' },
          ],
        }))}
      />
    </div>
  );
}

// One campaign's report. Rendered both on its own page and under the
// campaigns table, so the two can never drift apart.
export default function CampaignReport({ report, t, showName = true }) {
  const breakdownColumns = {
    sent: t('outreach.analytics.sent'),
    replies: t('outreach.analytics.replies'),
    replyRate: t('outreach.analytics.replyRate'),
    bounced: t('outreach.analytics.bounced'),
  };

  return (
    <section className="analytics-campaign">
      {showName && <h3 className="unibox-detail-subject">{report.campaign_name}</h3>}

      <div className="metric-row">
        <Metric label={t('outreach.analytics.replyRate')} value={percent(report.reply_rate)} tone="primary" />
        <Metric label={t('outreach.analytics.sent')} value={report.delivery.sent} />
        <Metric label={t('outreach.analytics.replies')} value={report.delivery.replies} />
        <Metric
          label={t('outreach.analytics.bounceRate')}
          value={percent(report.bounce_rate)}
          tone={report.bounce_rate > 0.03 ? 'alert' : undefined}
          hint={t('outreach.analytics.hints.bounceRate')}
        />
        <Metric
          label={t('outreach.analytics.opensHuman')}
          value={report.opens.enabled ? report.opens.human : t('outreach.analytics.trackingOff')}
          hint={t('outreach.analytics.hints.opensHuman')}
        />
        <Metric
          label={t('outreach.analytics.clicks')}
          value={
            report.clicks.enabled
              ? t('outreach.analytics.clicksValue', { total: report.clicks.total, unique: report.clicks.unique })
              : t('outreach.analytics.trackingOff')
          }
          hint={t('outreach.analytics.hints.clicks')}
        />
      </div>

      <details className="analytics-details">
        <summary>{t('outreach.analytics.breakdowns')}</summary>

        <h4 className="lead-detail-heading">
          {t('outreach.analytics.pipelineHeading')}
          <InfoHint text={t('outreach.analytics.hints.pipeline')} />
        </h4>
        <div className="metric-row">
          {Object.entries(report.pipeline.by_status).map(([key, count]) => (
            <Metric key={key} label={key} value={count} />
          ))}
          {Object.keys(report.pipeline.by_status).length === 0 && (
            <div className="unibox-empty">{t('outreach.analytics.noLeads')}</div>
          )}
        </div>

        <BreakdownTable
          rows={report.by_step}
          title={t('outreach.analytics.byStep')}
          columns={{ ...breakdownColumns, key: t('outreach.campaign.steps') }}
        />
        <BreakdownTable
          rows={report.by_variant}
          title={t('outreach.analytics.byVariant')}
          columns={{ ...breakdownColumns, key: t('outreach.analytics.variant') }}
        />
        <BreakdownTable
          rows={report.by_mailbox}
          title={t('outreach.analytics.byMailbox')}
          columns={{ ...breakdownColumns, key: t('outreach.mailboxes') }}
        />

        {report.opens.enabled && (
          <>
            <HourChart
              buckets={report.opens_by_hour}
              title={t('outreach.analytics.primeTime', { timezone: report.timezone })}
              emptyLabel={t('outreach.analytics.noHumanOpens')}
            />
            <h4 className="lead-detail-heading">{t('outreach.analytics.countries')}</h4>
            {report.opens_by_country.length === 0 && (
              <div className="unibox-empty">{t('outreach.analytics.noHumanOpens')}</div>
            )}
            {report.opens_by_country.map((bucket) => (
              <div key={bucket.country || 'unknown'} className="lead-detail-row">
                <span>{bucket.country || t('outreach.analytics.countryUnknown')}</span>
                <span className="unibox-badge">{bucket.opens}</span>
              </div>
            ))}
          </>
        )}
      </details>
    </section>
  );
}
